import {
  SupplierRelationshipEnumType,
  SupplierRelationshipPaymentDueCalculateFromType,
  SupplierRelationshipPaymentTermsType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  calculateItemTotal,
  calculateVatTotal,
  formatCurrency,
} from "@sellar-io/common";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router";
import { OrderSummaryContainer } from "../../../components";
import { OrderSummaryStatusContainer } from "../../../components/newOrderSummary/OrderSummaryStatus/OrderSummaryStatus";
import { ProfileImagePrompt } from "../../../components/profileImageUpload/ProfileImagePrompt";
import {
  CUSTOMER,
  ORDER_SUPPLIER,
  ORDER_BUYER,
  BUYER,
  PAYMENT_LINK_REF,
  SUPPLIERS,
} from "../../../constants/routes";
import {
  BasketContextActionEnum,
  useBasketContext,
  useSellarUser,
} from "../../../hooks";
import { usePriceListList } from "../../../hooks/api/v2/priceList/usePriceListList";
import { useUser } from "../../../hooks/api/v2/user/useUser";
import { useFeatureFlagStore } from "../../../hooks/useFeatureFlags";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useNavigateToChild } from "../../../hooks/useParentNavigation";
import { usePaymentLinkCreate } from "../../../hooks/api/v2/paymentLink/usePaymentLinkCreate";
import { renderTaxName } from "../../../utils/sellarHelpers";
import { usePaymentList } from "../../../hooks/api/v2/payment/usePaymentList";
import { isPromotionalOrderItem } from "../../stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { useOrderDistributionDeliveryArea } from "../../../hooks/api/v2/deliveryArea/useOrderDistributionDeliveryArea";
import { SharedOrderCards } from "../SharedOrderCards";
import {
  OrderActionsDropdown,
  OrderAlertBanners,
  OrderDialogs,
  getBuyerEmails,
  useOrderContentState,
} from "./components";

export type OrderContentConnectionsType = {
  id: number;
  approved: boolean;
  connectionType: SupplierRelationshipEnumType;
  buyerInvoiceEmails?: string[] | null;
  buyerContactEmail?: string | null;
  paymentTerms: SupplierRelationshipPaymentTermsType | null;
  paymentDueCalculateFrom: SupplierRelationshipPaymentDueCalculateFromType | null;
  paymentDue: number | null;
  PaymentTermsRequest: {
    paymentDueCalculateFrom: SupplierRelationshipPaymentDueCalculateFromType;
    paymentDue: number;
    status: "pending" | "accepted" | "rejected";
  } | null;
};

export const OrderContent = ({
  order,
  isLoading,
  connection,
}: {
  order: OrderGetResponseType;
  isLoading: boolean;
  connection?: OrderContentConnectionsType;
}) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const [, dispatchBasket] = useBasketContext();
  const { isActive } = useFeatureFlagStore();
  const remoteFeatureFlagQuery = useRemoteFeatureFlags();
  const isPaymentLinkRequestEnabled = remoteFeatureFlagQuery.isActive(
    "checkoutPaymentLinkRequest",
  );
  const paymentLinkCreateMutation = usePaymentLinkCreate();
  const canShowBuyerPaymentLinkAction =
    isPaymentLinkRequestEnabled &&
    order.paymentType === "manual" &&
    !order.paidAt &&
    Boolean(order.Supplier.stripeAccountId);
  const showSendCardPaymentLink =
    canShowBuyerPaymentLinkAction && !order.PaymentLink;
  const showCardPaymentLink =
    canShowBuyerPaymentLinkAction && Boolean(order.PaymentLink);

  const { state, actions } = useOrderContentState(order);

  const canUseSellarCreditFeatures = order.paymentType === "sellarCredit";

  if (!business) throw new Error("Cannot find business");

  const navigate = useNavigate();
  const navigateToChild = useNavigateToChild();
  const userQuery = useUser(parseInt(user?.id.toString() ?? ""), {
    enabled: !!user?.id,
  });

  const paymentsQuery = usePaymentList(
    {
      query: [
        {
          type: "OrderId",
          modifier: "EQUALS",
          value: order.id,
        },
      ],
      limit: 1,
    },
    {
      enabled: Boolean(order.paymentType === "checkout"),
    },
  );

  const payment = paymentsQuery.data?.pages[0]?.[0];

  const priceTiersQuery = usePriceListList(
    {},
    { enabled: business.type === "supplier" },
  );

  const { usesPalletInbound: supplierUsesPalletInboundForOrder } =
    useOrderDistributionDeliveryArea({
      fulfillmentType: order.fulfillmentType,
      supplierId: order.SupplierId,
      postcode: order.Retailer.Business.DeliveryAddress.postalCode,
    });

  const orderHasPalletOrderShipment = order.SupplierPalletShipment !== null;

  const [searchParams] = useSearchParams();

  const defaultPriceTierName = business.Supplier?.DefaultPriceTierId
    ? priceTiersQuery.data?.pages[0]?.find(
        (priceTier) => priceTier.id === business.Supplier?.DefaultPriceTierId,
      )?.name
    : undefined;

  const getOrderAction = (order: OrderGetResponseType) => {
    if (order.cancelledAt) return "cancelled";
    if (order.confirmedAt) return "confirmed";
    return "paid for";
  };

  const getOrderCannotBeEditedMessage = () => {
    if (business.Retailer)
      return `Only 'pending' orders can be edited. If you need to make changes or cancel this order, you can write a direct message to ${order.Supplier.Business.name} below.`;

    return `This order has been ${getOrderAction(
      order,
    )}, which means that it cannot be edited.`;
  };

  const editProductsDisabled = () => {
    const isOrderConfirmed = !!order.confirmedAt;
    const isOrderPaidNotCheckout =
      !!order.paidAt && order.paymentType !== "checkout";
    const isOrderCancelled = !!order.cancelledAt;

    return isOrderConfirmed || isOrderPaidNotCheckout || isOrderCancelled;
  };

  const showCancelFlow = () => {
    if (order.paymentType === "sellarCredit") {
      actions.setSellarCreditCancelWarningActive(true);
    } else {
      actions.setCancelOrderModalActive(true);
    }
  };

  const onEditOrder = () => {
    // If a payment has failed, we do not handle changing the failed payment intent on stripe
    const isCheckoutOrder = order.paymentType === "checkout";
    const isCheckoutNotPaid = order.paymentStatus !== "paid" && isCheckoutOrder;
    const isCheckoutV1 = isCheckoutOrder && order.checkoutVersion !== 2;
    const hasNotGotPartialRefundFeatureFlag =
      isCheckoutOrder && !isActive("ORDER_PARTIAL_REFUNDS");
    if (
      isCheckoutNotPaid ||
      isCheckoutV1 ||
      hasNotGotPartialRefundFeatureFlag
    ) {
      return actions.setCancelRecreateDialogActive(true);
    }

    if (editProductsDisabled()) {
      return actions.setOrderCannotBeEditedActive(true);
    }

    if (business.Supplier) {
      return navigate({
        pathname: "edit",
        search: searchParams.toString(),
      });
    }

    // We want to remove all promotional order items
    // because we can't guarantee that they'll exist
    const updatedOrderItems = order.OrderItems.filter((orderItem) => {
      return !isPromotionalOrderItem(orderItem);
    });

    dispatchBasket({
      type: BasketContextActionEnum.CHANGE_ORDER,
      order: {
        ...order,
        OrderItems: updatedOrderItems,
      },
    });

    navigate(
      business.Supplier
        ? ORDER_SUPPLIER.href({
            connectionId: connection?.approved ? connection.id : "",
          })
        : ORDER_BUYER.href({ supplierIdOrUrlId: order.Supplier.urlId }),
    );
  };

  const hasNoPayment =
    !payment || payment.status === "failed" || payment.status === "pending";

  const showMarkOrderWithIncompleteStripePaymentAsPaid =
    Boolean(order.confirmedAt) &&
    order.paymentType === "checkout" &&
    hasNoPayment;

  return (
    <>
      <div className={"flex flex-col gap-4 p-2"}>
        {!isLoading && (
          <OrderAlertBanners
            order={order}
            connection={connection}
            business={business}
            defaultPriceTierName={defaultPriceTierName}
            userEmail={user?.email}
            userName={user?.name ?? undefined}
          />
        )}
        <OrderSummaryStatusContainer
          order={order}
          buyerEmails={getBuyerEmails(order, connection)}
          onBusinessNameClicked={
            business.Supplier
              ? () =>
                  navigateToChild(
                    connection
                      ? CUSTOMER.href({ connectionId: connection.id })
                      : BUYER.href({ buyerId: order.RetailerId }),
                  )
              : () => navigate(`${SUPPLIERS}/${order.SupplierId}`)
          }
          connection={connection}
          dropDownChildren={
            <OrderActionsDropdown
              order={order}
              business={business}
              canUseSellarCreditFeatures={canUseSellarCreditFeatures}
              supplierUsesPalletInboundForOrder={
                supplierUsesPalletInboundForOrder
              }
              orderHasPalletOrderShipment={orderHasPalletOrderShipment}
              showMarkOrderWithIncompleteStripePaymentAsPaid={
                showMarkOrderWithIncompleteStripePaymentAsPaid
              }
              showSendCardPaymentLink={showSendCardPaymentLink}
              showCardPaymentLink={showCardPaymentLink}
              userEmail={user?.email}
              state={state}
              actions={actions}
              onCancelFlow={showCancelFlow}
              onSendCardPaymentLink={() =>
                paymentLinkCreateMutation.mutate(
                  { OrderId: order.id },
                  {
                    onSuccess: ({ code }) =>
                      navigate(PAYMENT_LINK_REF.href({ code })),
                  },
                )
              }
            />
          }
        />
        <OrderSummaryContainer
          showEditCopy={true}
          stripeInvoiceId={order.stripeInvoiceId}
          onViewOrderHistory={() => actions.setEventListDialogActive(true)}
          supplierName={order.Supplier.Business.name}
          confirmedAt={order.confirmedAt}
          checkoutVersion={order.checkoutVersion}
          cancelledAt={order.cancelledAt}
          paymentDue={order.paymentDue}
          paymentType={order.paymentType}
          paymentTerms={connection?.paymentTerms}
          onEditClick={onEditOrder}
          items={order.OrderItems.map((orderItem) => ({
            description: orderItem.Product.name ?? "",
            quantity: orderItem.quantity,
            cost: formatCurrency(orderItem.price),
            sku: orderItem.Product.sku,
            fullPrice: orderItem.fullPrice,
            price: orderItem.price,
            ProductId: orderItem.ProductId,
            ParentId: orderItem.Product.ParentId,
            promotionAppliedAt: orderItem.promotionAppliedAt,
          }))}
          priceProps={{
            delivery: formatCurrency(order.deliveryCharge, order.currency),
            isDeliveryChargeCapped: order.deliveryChargeCapped,
            subTotal: order.OrderItems
              ? formatCurrency(
                  calculateItemTotal(order.OrderItems),
                  order.currency,
                )
              : "",
            discount:
              order.discountAmount > 0
                ? {
                    value: formatCurrency(order.discountAmount, order.currency),
                    calculatedTotal: formatCurrency(
                      order.discountAmount,
                      order.currency,
                    ),
                  }
                : undefined,
            tax: {
              name: renderTaxName(order.currency),
              percentage: order.vatPercentage.toString(),
              total: formatCurrency(
                calculateVatTotal({ ...order, orderItems: order.OrderItems }),
                order.currency,
              ),
            },
            total: formatCurrency(order.price, order.currency),
          }}
        />
        <SharedOrderCards
          order={order}
          connection={connection}
          buyer={order.Retailer}
        />
        {userQuery.data && <ProfileImagePrompt user={userQuery.data} />}
      </div>
      <OrderDialogs
        order={order}
        connection={connection}
        business={business}
        state={state}
        actions={actions}
        getOrderCannotBeEditedMessage={getOrderCannotBeEditedMessage}
        onEditOrder={onEditOrder}
      />
    </>
  );
};
